import { getDataAll as apiGetDataAll } from 'api/Api'
import {
  add as addCard,
} from 'routes/Board/redux/cards'
import {
  init,
  setLoadStatus,
} from '../redux/view'
import {
  addArray as addObserversArray
} from '../redux/observers'
import {
  addArray as addVisitorsArray
} from '../redux/visitors'
import {
  addArray as addElementsArray
} from '../redux/elements'
import {
  addArray as addChecklistItemsArray
} from '../redux/checklistItems'
import {
  addArray as addDocumentListItemsArray
} from '../redux/documentListItems'
import {
  addArray as addSubCardsArray
} from '../redux/subCards'
import {
  removeAllForCardId as removeAllCardBlockersForCardId,
  addArray as addCardBlockerArray,
} from 'store/redux/cardBlockers'
import {
  add as addBlocker,
} from 'store/redux/blockers'
import viewError from 'store/sagas/viewError'

type Payload = {
  cardId: number
}

export default (payload: Payload) => async (dispatch, getState) => {
  const { cardId } = payload

  dispatch([
    init({
      cardId,
    }),
    setLoadStatus({
      cardId,
      isLoading: true,
      isLoaded: false
    }),
    removeAllCardBlockersForCardId({
      cardId,
    })
  ])

  try {
    const [
      Card,
      {
        CardBlockers
      }
    ] = await apiGetDataAll([
      {
        method: 'Card',
        args: {
          id: cardId,
          loadFiles: false,
        }
      },
      {
        method: 'CardBlockers',
        args: {
          id: cardId,
        }
      },
    ])

    const batchedActions: any[] = []

    // Followers
    batchedActions.push(addObserversArray({
      cardId,
      array: Card.Observers
    }))

    // Visitors
    const visitors = Card.Visits.map(Visitor => {
      return {
        id: Visitor.UserId,
        timestamp: Visitor.Timestamp
      }
    })

    batchedActions.push(addVisitorsArray({
      cardId,
      array: visitors
    }))

    // Elements
    const elements: any[] = []
    const checklistItems: any[] = []
    const documents: any[] = []
    const subCards: any[] = []

    Card.Elements.forEach(Element => {
      let element: any = {
        id: Element.Id,
        elementName: Element.ElementName,
        name: Element.Name
      }

      if (Element.ElementName === '/Data/Note') {
        element = {
          ...element,
          content: JSON.parse(Element.Text)
        }
      } else if (Element.ElementName === '/Data/Checklist') {
        element = {
          ...element,
          items: Element.Items.map(Item => {
            checklistItems.push({
              id: Item.Id,
              name: JSON.parse(Item.Name),
              completed: Item.Completed,
              userId: Item.UserId,
              dueDate: Item.Timestamp
            })

            return Item.Id
          })
        }
      } else if (Element.ElementName === '/Data/DocumentsList') {
        element = {
          ...element,
          items: Element.Items.map(Document => {
            documents.push({
              id: Document.Id,
              url: Document.Url,
              name: Document.Name,
              sourceId: Document.SourceId,
              exists: Document.Exists,
              position: Document.Position,
              isLoaded: false,
              isLoading: false,
            })

            return Document.Id
          })
        }
      } else if (Element.ElementName === '/Data/DocumentsFolder') {
        element = {
          ...element,
          documentSourceId: Element.DocumentSourceId,
          path: Element.Path,
          fullUrl: Element.FullUrl,
          settings: Element.Settings,
        }
      } else if (Element.ElementName === '/Data/DocumentsFolderCreator') {
        element = {
          ...element,
          documentSourceId: Element.DocumentSourceId,
          path: Element.Path,
          fullUrl: Element.FullUrl,
          settings: Element.Settings,
        }
      } else if (Element.ElementName === '/Data/SubCardGroup') {
        element = {
          ...element,
          items: Element.Items.map(Item => {
            subCards.push({
              id: Item.Id,
              cardId: Item.ChildId,
            })

            return Item.Id
          })
        }
      }

      elements.push(element)
    })

    batchedActions.push(addChecklistItemsArray({
      cardId,
      array: checklistItems
    }))
    batchedActions.push(addDocumentListItemsArray(documents))
    batchedActions.push(addSubCardsArray({
      cardId,
      array: subCards
    }))
    batchedActions.push(addElementsArray({
      cardId,
      array: elements
    }))

    // Card blockers
    batchedActions.push(CardBlockers.map(CardBlocker => addBlocker({
      id: CardBlocker.BlockerId,
      type: CardBlocker.BlockerType,
      name: CardBlocker.Name,
      description: CardBlocker.Description && JSON.parse(CardBlocker.Description),
      position: CardBlocker.Position,
    })))
    batchedActions.push(addCardBlockerArray({
      cardBlockers: CardBlockers.map(CardBlocker => ({
        id: CardBlocker.Id,
        blockerId: CardBlocker.BlockerId,
        cardId: CardBlocker.CardId,
        reason: CardBlocker.Reason,
        userId: CardBlocker.UserId,
        creationDate: CardBlocker.CreationDate,
        createdBy: CardBlocker.CreatedBy,
      })),
    }))

    batchedActions.push(addCard({
      id: Card.Id,
      boardId: Card.BoardId,
      listId: Card.ListId,
      name: Card.Name,
      responsible: Card.Users.filter(User => User.IsResponsible).map(User => User.Id)[0] || null,
      users: Card.Users.filter(User => !User.IsResponsible).map(User => User.Id),
      labels: Card.Labels.map(Label => Label.Id),
      dueDate: Card.DueDate,
      dueDateId: Card.DueDateId,
      isArchived: Card.IsArchived,
    }))

    batchedActions.push(setLoadStatus({
      cardId,
      isLoading: false,
      isLoaded: true
    }))

    dispatch(batchedActions)
  } catch (error) {
    if (error.code === 401) {
      const state = getState()
      dispatch(viewError({
        text: state.kanbo.locale.messages['CARD_NOT_FOUND']
      }))
    } else if (error.code === 601) {
      const state = getState()
      dispatch(viewError({
        text: state.kanbo.locale.messages['CARD_ACCESS_DENIED']
      }))
    } else if (error.code !== 517) {
      dispatch(viewError({
        error,
        refresh: true
      }))
    }

    dispatch(setLoadStatus({
      cardId,
      isLoading: false,
      isLoaded: false
    }))
  }
}
